import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  addNoteAction,
  closeLeadAction,
  fetchSingleLeadAction,
  updateLeadAction,
} from "./leadAction";
import {
  Row,
  Col,
  Button,
  Container,
  Form,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { NoData } from "../../components/no-data/NoData";
import DefaultLayout from "../../components/layout/DefaultLayout";

const LeadLanding = () => {
  const { _id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sendEmailRef = useRef();
  const sendSMSRef = useRef();
  const noteRef = useRef();
  const subjectRef = useRef();
  const reminderTitleRef = useRef();
  const reminderDateRef = useRef();
  const reminderTimeRef = useRef();

  const [showSubject, setShowSubject] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  const { user } = useSelector((state) => state.user);
  const { selectedLead, isLoading } = useSelector((state) => state.studentLead);

  useEffect(() => {
    if (_id) dispatch(fetchSingleLeadAction(_id));
  }, [_id, dispatch]);

  if (!selectedLead?._id) return <NoData />;

  const handleSubmit = () => {
    if (!noteRef.current?.value) {
      toast.warn("Please add some message");
      return;
    }

    dispatch(
      addNoteAction({
        _id,
        message: noteRef.current.value,
        sendEmailFromNote: sendEmailRef.current?.checked,
        sendSMSFromNote: sendSMSRef.current?.checked,
        subject: subjectRef.current?.value,
        email: selectedLead.email,
        fName: selectedLead.fName,
        phone: selectedLead.phone,
      })
    );

    noteRef.current.value = "";
    setShowSubject(false);
  };

  const handleSetReminder = (e) => {
    e.preventDefault();
    const title = reminderTitleRef.current?.value;
    const date = reminderDateRef.current?.value;
    const time = reminderTimeRef.current?.value;

    if (!date) {
      toast.error("Please set a date for the reminder!");
      return;
    }

    dispatch(
      updateLeadAction({
        title,
        date,
        time,
        _id,
        addedBy: { userId: user?._id, name: `${user.fName} ${user.lName}` },
      })
    );

    reminderTitleRef.current.value = "";
    reminderDateRef.current.value = "";
    reminderTimeRef.current.value = "";
  };

  return (
    <DefaultLayout>
      <Container className="page-lead">
        <Row>
          <Col>
            {isLoading && <Spinner animation="border" variant="primary" />}
            <div className="back py-2 d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/leads")}
              >
                &lt; Back
              </Button>

              <Button
                variant="outline-danger"
                onClick={() => dispatch(closeLeadAction(selectedLead._id))}
              >
                Mark as Closed
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="lead-info text-small mt-3">
              <ul>
                <li>
                  <span
                    className={
                      "status " +
                      (selectedLead?.progress === "inprogress"
                        ? "bg-info"
                        : selectedLead?.progress === "closed"
                        ? "bg-success"
                        : "bg-warning")
                    }
                  >
                    Status: {selectedLead?.progress}
                  </span>
                </li>
                <li>
                  Name: {selectedLead?.fName} {selectedLead?.lName}
                </li>
                <li>Email: {selectedLead?.email}</li>
                <li>Phone: {selectedLead?.phone}</li>
                <li className="text-capitalize">
                  Location: {selectedLead?.location}
                </li>
                <li>
                  Added By:{" "}
                  {selectedLead?.addedBy?.name || selectedLead?.agent?.name}
                </li>
              </ul>
            </div>
          </Col>

          <Col>
            <div className="my-3">
              <h6 className="fw-bold">Existing Reminder</h6>
              <Table
                striped
                bordered
                hover
                responsive
                style={{ fontSize: "12px" }}
              >
                <thead className="bg-info">
                  <tr>
                    <th>Schedule Date</th>
                    <th>Time (24hr)</th>
                    <th>Added By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {selectedLead?.reminder?.date
                        ? new Date(
                            selectedLead.reminder.date
                          ).toLocaleDateString("en-AU")
                        : "—"}
                    </td>
                    <td>{selectedLead?.reminder?.time || "—"}</td>
                    <td>{selectedLead?.reminder?.addedBy?.name || "—"}</td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <div className="mb-3">
              <h6 className="fw-bold">Set Reminder</h6>
              <Form.Group>
                <Form.Check
                  type="radio"
                  label="No reminder"
                  name="reminder"
                  onChange={() => setShowReminder(false)}
                  defaultChecked
                />
                <Form.Check
                  type="radio"
                  label="Schedule a reminder"
                  name="reminder"
                  onChange={() => setShowReminder(true)}
                />
              </Form.Group>

              {showReminder && (
                <Form
                  className="my-3 d-flex flex-column gap-2"
                  onSubmit={handleSetReminder}
                >
                  <Form.Group>
                    <Form.Label className="fw-bold text-small">
                      Title
                    </Form.Label>
                    <Form.Control type="text" ref={reminderTitleRef} />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="fw-bold text-small">
                      Select time
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control type="date" ref={reminderDateRef} />
                      <Form.Control type="time" ref={reminderTimeRef} />
                    </div>
                  </Form.Group>

                  <div className="mt-1 ms-auto">
                    <Button variant="warning" type="submit">
                      Save
                    </Button>
                  </div>
                </Form>
              )}
            </div>
          </Col>
        </Row>

        <hr />

        <Row>
          <Col>
            <div className="message-history">
              {selectedLead.note?.length ? (
                selectedLead.note.map(
                  (msg, i) =>
                    msg.message && (
                      <div className="message-history-list" key={i}>
                        <div className="mb-1">
                          {msg.addedBy} -{" "}
                          <small>
                            {new Date(msg.addedAt).toLocaleDateString()}
                          </small>
                        </div>
                        <div className="lead-message">
                          {msg.message}{" "}
                          {msg.tag && (
                            <span className="lead-message-tag">
                              <i className="fa-solid fa-circle-check text-success"></i>
                              <i>{msg.tag}</i>
                            </span>
                          )}
                        </div>
                      </div>
                    )
                )
              ) : (
                <p>No messages yet.</p>
              )}
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="reply-msg mt-5 mb-5">
              <Form.Group as={Col}>
                <Form.Label>Reply</Form.Label>
                <Form.Control
                  ref={noteRef}
                  as="textarea"
                  rows={3}
                  className="my-3"
                  placeholder="'Dear clientName' in the header and 'Dented Code info' in footer is automatically added. Please only add the main body text."
                />

                {showSubject && (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    Subject:
                    <Form.Control
                      type="text"
                      placeholder="Enter subject for email..."
                      ref={subjectRef}
                    />
                  </div>
                )}

                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Form.Check
                      ref={sendEmailRef}
                      onChange={(e) => setShowSubject(e.target.checked)}
                    />
                    <span>Send as email</span>
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-3 text-danger">
                    <Form.Check ref={sendSMSRef} />
                    <span>Send as SMS</span>
                  </div>
                </div>

                <Button onClick={handleSubmit} variant="primary">
                  Reply
                </Button>
              </Form.Group>
            </div>
          </Col>
        </Row>
      </Container>
    </DefaultLayout>
  );
};

export default LeadLanding;
